import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { IN8NWorkflow } from "../../models/n8n-workflow";
import { IBasePaging } from "../../models/common/models.type";
import useAxios from "../../components/UseAxios/useAxios";
import N8NWorkflowService from "../../services/api/n8n-workflow.service";
import { toggleMessage } from "../../components/Toast/Toast";
import LoadingOverlayWrapper from "react-loading-overlay-ts";

// Components
import WorkflowsTable from "./components/WorkflowsTable/WorkflowsTable";
import WorkflowForm from "./components/WorkflowForm/WorkflowForm";
import ExecutionHistory from "./components/ExecutionHistory/ExecutionHistory";
import WorkflowDetailDrawer from "./components/WorkflowDetailDrawer/WorkflowDetailDrawer";
import DeleteConfirmDialog from "./components/DeleteConfirmDialog/DeleteConfirmDialog";

enum TabCode {
  LIST = "list",
  HISTORY = "history"
}

const N8NWorkflows: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState<string>(TabCode.LIST);
  const [workflows, setWorkflows] = useState<IN8NWorkflow[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<IN8NWorkflow | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [paging, setPaging] = useState<IBasePaging>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0
  });

  // API hooks
  const getWorkflowsAxios = useAxios<IN8NWorkflow[]>({
    loading: "OnRequest"
  });
  const getWorkflowAxios = useAxios<IN8NWorkflow>({ loading: "OnRequest" });
  const deleteWorkflowAxios = useAxios<void>({ loading: "OnRequest" });
  const executeWorkflowAxios = useAxios<any>({ loading: "OnRequest" });
  const activateWorkflowAxios = useAxios<IN8NWorkflow>({ loading: "OnRequest" });

  // Initialize tab from URL
  useEffect(() => {
    const tab = searchParams.get("tab") || TabCode.LIST;
    setCurrentTab(tab);
  }, [searchParams]);

  // Fetch workflows
  useEffect(() => {
    fetchWorkflows();
  }, [paging.currentPage, searchQuery]);

  const fetchWorkflows = () => {
    getWorkflowsAxios.request(
      N8NWorkflowService.getWorkflows({
        currentPage: paging.currentPage,
        pageSize: paging.pageSize,
        search: searchQuery
      })
    );
  };

  useEffect(() => {
    if (getWorkflowsAxios.isSuccess && getWorkflowsAxios.data) {
      console.log('getWorkflowsAxios=============::::::::', getWorkflowsAxios.data);
      setWorkflows(getWorkflowsAxios.data || []);
      if (getWorkflowsAxios.paging) {
        setPaging(getWorkflowsAxios.paging);
      }
    }
    if (getWorkflowsAxios.error) {
      toggleMessage({
        type: "error",
        message: getWorkflowsAxios.error?.message || "Failed to fetch workflows"
      });
    }
  }, [getWorkflowsAxios.isSuccess, getWorkflowsAxios.error, getWorkflowsAxios.data, getWorkflowsAxios.paging]);

  // Delete workflow effect
  useEffect(() => {
    if (deleteWorkflowAxios.isSuccess) {
      toggleMessage({ type: "success", message: "Workflow deleted successfully!" });
      setIsDeleteDialogOpen(false);
      setSelectedWorkflow(null);
      fetchWorkflows();
    }
    if (deleteWorkflowAxios.error) {
      toggleMessage({
        type: "error",
        message: deleteWorkflowAxios.error?.message || "Failed to delete workflow"
      });
    }
  }, [deleteWorkflowAxios.isSuccess, deleteWorkflowAxios.error]);

  // Execute workflow effect
  useEffect(() => {
    if (executeWorkflowAxios.isSuccess) {
      toggleMessage({ type: "success", message: "Workflow executed successfully!" });
      setIsDetailDrawerOpen(false);
      fetchWorkflows();
    }
    if (executeWorkflowAxios.error) {
      toggleMessage({
        type: "error",
        message: executeWorkflowAxios.error?.message || "Failed to execute workflow"
      });
    }
  }, [executeWorkflowAxios.isSuccess, executeWorkflowAxios.error]);

  // Activate workflow effect
  useEffect(() => {
    if (activateWorkflowAxios.isSuccess) {
      toggleMessage({ type: "success", message: "Workflow status updated successfully!" });
      fetchWorkflows();
    }
    if (activateWorkflowAxios.error) {
      toggleMessage({
        type: "error",
        message: activateWorkflowAxios.error?.message || "Failed to update workflow status"
      });
    }
  }, [activateWorkflowAxios.isSuccess, activateWorkflowAxios.error]);

  // Get single workflow effect (for detail drawer with fresh execution stats)
  useEffect(() => {
    if (getWorkflowAxios.isSuccess && getWorkflowAxios.data) {
      setSelectedWorkflow(getWorkflowAxios.data);
    }
    if (getWorkflowAxios.error) {
      toggleMessage({
        type: "error",
        message: getWorkflowAxios.error?.message || "Failed to fetch workflow details"
      });
    }
  }, [getWorkflowAxios.isSuccess, getWorkflowAxios.error, getWorkflowAxios.data]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
    setSearchParams({ tab: newValue });
  };

  const handleCreateNew = () => {
    setSelectedWorkflow(null);
    setEditMode(false);
    setIsFormModalOpen(true);
  };

  const handleEdit = (workflow: IN8NWorkflow) => {
    setSelectedWorkflow(workflow);
    setEditMode(true);
    setIsDetailDrawerOpen(false);
    setIsFormModalOpen(true);
  };

  const handleView = (workflow: IN8NWorkflow) => {
    // Set initial workflow data for immediate display
    setSelectedWorkflow(workflow);
    setIsDetailDrawerOpen(true);
    // Fetch fresh data with execution stats from n8n
    getWorkflowAxios.request(
      N8NWorkflowService.getWorkflow(workflow._id)
    );
  };

  const handleDeleteClick = (workflow: IN8NWorkflow) => {
    setSelectedWorkflow(workflow);
    setIsDeleteDialogOpen(true);
    setIsDetailDrawerOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (selectedWorkflow) {
      deleteWorkflowAxios.request(
        N8NWorkflowService.deleteWorkflow(selectedWorkflow._id)
      );
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setSelectedWorkflow(null);
  };

  const handleExecute = (workflow: IN8NWorkflow) => {
    executeWorkflowAxios.request(
      N8NWorkflowService.executeWorkflow(workflow._id)
    );
  };

  const handleActivate = (workflow: IN8NWorkflow, active: boolean) => {
    activateWorkflowAxios.request(
      N8NWorkflowService.activateWorkflow(workflow._id, active)
    );
  };

  const handleFormSuccess = () => {
    setIsFormModalOpen(false);
    setSelectedWorkflow(null);
    setEditMode(false);
    fetchWorkflows();
  };

  const handleFormCancel = () => {
    setIsFormModalOpen(false);
    setSelectedWorkflow(null);
    setEditMode(false);
  };

  const handlePageChange = (page: number) => {
    setPaging({ ...paging, currentPage: page });
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPaging({ ...paging, currentPage: 1 });
  };

  const handleDrawerClose = () => {
    setIsDetailDrawerOpen(false);
    setSelectedWorkflow(null);
  };

  return (
    <LoadingOverlayWrapper
      active={
        getWorkflowsAxios.isLoading ||
        deleteWorkflowAxios.isLoading ||
        executeWorkflowAxios.isLoading ||
        activateWorkflowAxios.isLoading
      }
      spinner
    >
      <Box sx={{ width: "100%" }}>
        <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2.5,
              pb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #e0e0e0"
            }}
          >
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ color: "#05224e" }}
            >
              N8N Workflow Management
            </Typography>
            {currentTab === TabCode.LIST && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Add sx={{ fontSize: 18 }} />}
                onClick={handleCreateNew}
                sx={{
                  backgroundColor: "#05224e",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  px: 2,
                  py: 0.75,
                  borderRadius: 1.5,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#0a3a7d",
                    boxShadow: "0 2px 8px rgba(5, 34, 78, 0.25)"
                  }
                }}
              >
                Create Workflow
              </Button>
            )}
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2.5 }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  minHeight: 44,
                  color: "#666",
                  "&.Mui-selected": {
                    color: "#05224e"
                  }
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#05224e"
                }
              }}
            >
              <Tab label="Workflows" value={TabCode.LIST} />
              <Tab label="Execution History" value={TabCode.HISTORY} />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box>
            {currentTab === TabCode.LIST && (
              <WorkflowsTable
                workflows={workflows}
                loading={getWorkflowsAxios.isLoading}
                paging={paging}
                onPageChange={handlePageChange}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                onExecute={handleExecute}
                onActivate={handleActivate}
                onSearch={handleSearch}
              />
            )}

            {currentTab === TabCode.HISTORY && (
              <ExecutionHistory workflows={workflows} />
            )}
          </Box>
        </Paper>

        {/* Form Modal */}
        <Dialog
          open={isFormModalOpen}
          onClose={handleFormCancel}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              paddingTop: '10px'
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', pb: 1 }}>
            {editMode ? 'Edit Workflow' : 'Create New Workflow'}
          </DialogTitle>
          <DialogContent sx={{ pt: 10 }}>
            <WorkflowForm
              workflow={selectedWorkflow}
              editMode={editMode}
              onSuccess={handleFormSuccess}
              onCancel={handleFormCancel}
            />
          </DialogContent>
        </Dialog>

        {/* Detail Drawer */}
        <WorkflowDetailDrawer
          open={isDetailDrawerOpen}
          workflow={selectedWorkflow}
          onClose={handleDrawerClose}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onExecute={handleExecute}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={isDeleteDialogOpen}
          workflow={selectedWorkflow}
          loading={deleteWorkflowAxios.isLoading}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      </Box>
    </LoadingOverlayWrapper>
  );
};

export default N8NWorkflows;